import { Button, ButtonKind, Column, marginHorizontal, paddingHorizontal, paddingVertical } from "@gadgetinc/widgets";
import { AddIcon } from "@gadgetinc/widgets/src/icons/AddIcon";
import { AppsFilledIcon, AppsIcon } from "@gadgetinc/widgets/src/icons/AppsIcon";
import { CardboardBoxFilledIcon, CardboardBoxIcon } from "@gadgetinc/widgets/src/icons/CardboardBoxIcon";
import { CircleDollarFilledIcon, CircleDollarIcon } from "@gadgetinc/widgets/src/icons/CircleDollarIcon";
import { HelpIcon } from "@gadgetinc/widgets/src/icons/HelpIcon";
import { PrototypeFilledIcon, PrototypeIcon } from "@gadgetinc/widgets/src/icons/PrototypeIcon";
import { UsageMeterFilledIcon, UsageMeterIcon } from "@gadgetinc/widgets/src/icons/SpeedIcon";
import { UserGroupFilledIcon, UserGroupIcon } from "@gadgetinc/widgets/src/icons/UserGroupIcon";
import { useStyletron } from "baseui";
import { observer } from "mobx-react-lite";
import type { ReactNode } from "react";
import React from "react";
import type { InitialBillingPlanProps, TeamProps } from "state-trees/src/Team";
import { Link, useLocation } from "wouter";
import { MenuItem } from "../edit/workspace/LeftNavBar/MenuItem";
import { TeamDropdown } from "./TeamDropdown";
import { UserDropdown } from "./UserDropdown";

const NavItem = (props: {
  label: React.ReactNode;
  testId: string;
  icon: ReactNode;
  iconOnSelected?: ReactNode;
  href: string;
  external?: boolean;
}) => {
  const [_css, $theme] = useStyletron();
  const [location, setLocation] = useLocation();
  const selected = location === props.href;
  const icon = selected ? props.iconOnSelected ?? props.icon : props.icon;

  return (
    <MenuItem
      testId={props.testId}
      href={props.href}
      selected={selected}
      onClick={(event) => {
        event.preventDefault();
        if (props.external) {
          window.open(props.href, "_blank");
        } else {
          const currentUrl = new URL(window.location.href);
          const newUrl = new URL(props.href, window.location.origin);

          currentUrl.searchParams.forEach((value, key) => {
            if (!newUrl.searchParams.has(key)) {
              newUrl.searchParams.set(key, value);
            }
          });

          setLocation(newUrl.pathname + newUrl.search + newUrl.hash);
        }
      }}
      icon={icon}
      $style={{
        ...paddingVertical($theme.sizing.scale200),
        flex: 1,
      }}
      external={props.external}
      overrides={{
        Body: {
          style: {
            color: selected ? $theme.colors.primary : $theme.colors.primary600,
          },
        },
        Row: {
          style: {
            flex: 1,
          },
        },
      }}
    >
      {props.label}
    </MenuItem>
  );
};

type AuthNavBarProps = {
  hasWideContent?: boolean | string;
  userEmail?: string;
  docsRootURL: string;
  isStaff?: boolean;
} & (
  | {
      team?: undefined;
      otherTeams?: undefined;
      initialPlan?: undefined;
    }
  | {
      team: TeamProps;
      otherTeams: TeamProps[];
      initialPlan: InitialBillingPlanProps;
    }
);

export const AuthLeftNavBar = observer((props: AuthNavBarProps) => {
  const [_, $theme] = useStyletron();

  // Construct create-app URL with current search params + reset=true
  const createAppUrl = React.useMemo(() => {
    const searchParams = new URLSearchParams(typeof window !== "undefined" ? window?.location.search || "" : "");
    searchParams.set("reset", "true");
    return `/auth/team/create-app?${searchParams.toString()}`;
  }, []);

  return (
    <Column
      $style={{
        height: "100vh",
        borderRight: `1px solid ${$theme.colors.alpha300}`,
        backgroundColor: $theme.colors.primary50,
        ...paddingVertical($theme.sizing.scale500),
      }}
      $align="space-between"
    >
      {/* Top section */}
      <div
        style={{
          width: "260px",
        }}
      >
        <Column $gap={$theme.sizing.scale600}>
          <Column
            $gap={$theme.sizing.scale600}
            $style={{
              ...paddingHorizontal($theme.sizing.scale500),
            }}
          >
            {props.team && <TeamDropdown team={props.team} otherTeams={props.otherTeams} initialPlan={props.initialPlan} />}
            <Link href={createAppUrl}>
              <Button startEnhancer={<AddIcon />} kind={ButtonKind.secondary}>
                Create app
              </Button>
            </Link>

            <Column>
              <NavItem label="Apps" testId="apps-nav-item" icon={<AppsIcon />} iconOnSelected={<AppsFilledIcon />} href="/auth/team" />
              <NavItem
                label="Members"
                testId="members-nav-item"
                icon={<UserGroupIcon />}
                iconOnSelected={<UserGroupFilledIcon />}
                href="/auth/team/members"
              />
              <NavItem
                label="Usage"
                testId="usage-nav-item"
                icon={<UsageMeterIcon />}
                iconOnSelected={<UsageMeterFilledIcon />}
                href="/auth/team/billing#usage"
              />
              <NavItem
                label="Billing"
                testId="billing-nav-item"
                icon={<CircleDollarIcon color="#545454" />}
                iconOnSelected={<CircleDollarFilledIcon />}
                href="/auth/team/billing"
              />
              <NavItem
                label={"Plan"}
                testId="plan-nav-item"
                icon={<CardboardBoxIcon />}
                iconOnSelected={<CardboardBoxFilledIcon />}
                href="/auth/team/billing#plans"
              />
            </Column>
          </Column>

          <hr
            style={{
              border: `1px solid ${$theme.colors.alpha300}`,
              borderBottom: "none",
            }}
          />

          <Column
            $style={{
              ...paddingHorizontal($theme.sizing.scale500),
            }}
          >
            {props.isStaff && (
              <NavItem
                label="Templates"
                testId="templates-nav-item"
                icon={<PrototypeIcon />}
                iconOnSelected={<PrototypeFilledIcon />}
                href="/auth/team/templates"
              />
            )}
            <NavItem label="Guide" testId="guide-nav-item" icon={<HelpIcon />} external href={`${props.docsRootURL}/guides`} />
          </Column>
        </Column>
      </div>

      {/* Bottom section */}
      {props.userEmail && (
        <div>
          <UserDropdown
            userEmail={props.userEmail}
            fullWidth
            $style={{
              ...marginHorizontal($theme.sizing.scale500),
            }}
            dropdownOverrides={{
              menu: {
                List: {
                  style: {
                    width: "236px",
                    overflow: "auto",
                    boxShadow: "none",
                    boxSizing: "content-box",
                    paddingTop: $theme.sizing.scale200,
                    paddingBottom: $theme.sizing.scale200,
                  },
                },
              },
            }}
            overrideArrowDirection="up"
          />
        </div>
      )}
    </Column>
  );
});
